import { useCallback } from 'react';
import { type LatLng } from '~/_shared/types/latLng';
import {
  type GroupRadiusProximity, isDriveTimePolygon, isGroupRadius, isIndividualRadius, type Proximity,
} from '~/_shared/types/proximity/proximity.types';
import { type SpreadsheetRowId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { areSpreadsheetRowIdsEqual } from '~/_shared/utils/spreadsheet/generalSpreadsheet.helpers';
import { notNull } from '~/_shared/utils/typeGuards';
import {
  areRadialDistancesEqual,
  findRowIdsToRenderForColumnIdInGroup, getGroupRadiusProximityDetails, isDtpAtLocation, isRadiusAtLocation,
} from '~/proximity/proximity.helpers';
import type { OverlappingProximity } from '~/store/frontendState/activeMapElements/activeMapElements.state';
import { useHiddenProximityIdsSelector } from '~/store/mapSettings/toolsState/visibility/visibility.selectors';
import { checkIfSpreadsheetRowIdMeetsFilteringCriteria } from '~/store/spreadsheetData/spreadsheetData.helpers';
import {
  useFilteredSpreadsheetRowIds, useLatLngSpreadsheetData, useSpreadSheetData,
} from '../useSpreadsheetData.hook';

type GroupRadiusProximityLocation = Readonly<{ spreadsheetRowId: SpreadsheetRowId; latLng: LatLng | null }>;
export type GroupRadiusProximityLocations = ReadonlyArray<GroupRadiusProximityLocation>;

export const useProximityLocation = () => {
  const spreadsheetData = useSpreadSheetData().spreadsheetData;
  const latLngData = useLatLngSpreadsheetData();
  const { filteredRowIds } = useFilteredSpreadsheetRowIds();
  const hiddenProximityIds = useHiddenProximityIdsSelector();

  const getGroupRadiusProximityLocations = useCallback((proximity: GroupRadiusProximity, ignoreFilters: boolean = false): GroupRadiusProximityLocations => {
    const { columnId, group: groupId } = proximity.data.data;
    const rowIds = findRowIdsToRenderForColumnIdInGroup(spreadsheetData, columnId, groupId);

    return rowIds.map((rowId) => {
      const spreadsheetRowId = {
        spreadsheetId: columnId.spreadsheetId,
        rowId,
      };

      if (!ignoreFilters && !checkIfSpreadsheetRowIdMeetsFilteringCriteria(filteredRowIds, spreadsheetRowId)) {
        return { spreadsheetRowId, latLng: null };
      }

      const latLng = latLngData.getRow(spreadsheetRowId);

      return {
        spreadsheetRowId,
        latLng: latLng ?? null,
      };
    }).filter(notNull);

  }, [filteredRowIds, latLngData, spreadsheetData]);

  const getProximityEntitiesAtLocation = useCallback((point: LatLng, proximity: Proximity) => {
    if (isIndividualRadius(proximity)) {
      return isRadiusAtLocation({ point, center: proximity.data.data, proximity })
        ? [{ proximityId: proximity.id }]
        : [];
    }

    if (isGroupRadius(proximity)) {
      return getGroupRadiusProximityLocations(proximity)
        .filter(loc => {
          const individualRadiusSettings = getGroupRadiusProximityDetails(proximity, loc.spreadsheetRowId);
          const overridenProximity = areRadialDistancesEqual(proximity.data, individualRadiusSettings)
            ? proximity
            : {
              ...proximity,
              data: {
                ...proximity.data,
                ...individualRadiusSettings,
              },
            };

          return loc.latLng && isRadiusAtLocation({ point, center: loc.latLng, proximity: overridenProximity });
        })
        .map(loc => ({
          proximityId: proximity.id,
          spreadsheetRowId: loc.spreadsheetRowId,
        }));
    }

    if (isDriveTimePolygon(proximity)) {
      return isDtpAtLocation(point, proximity)
        ? [{ proximityId: proximity.id }]
        : [];
    }

    return [];
  }, [getGroupRadiusProximityLocations]);

  const isProximityAtLocation = useCallback((
    point: LatLng,
    proximity: Proximity,
    specificGroupProximityRowId?: SpreadsheetRowId,
  ) => {
    const entitiesAtLocation = getProximityEntitiesAtLocation(point, proximity);

    if (specificGroupProximityRowId) {
      return entitiesAtLocation.some(e =>
        e.proximityId === proximity.id &&
        areSpreadsheetRowIdsEqual(e.spreadsheetRowId, specificGroupProximityRowId)
      );
    }

    return entitiesAtLocation.length > 0;
  }, [getProximityEntitiesAtLocation]);

  const getProximityIdsAtLocation = useCallback((
    point: LatLng,
    proximities: ReadonlyArray<Proximity>,
  ): OverlappingProximity[] => {
    return proximities
      .filter(p => !hiddenProximityIds.includes(p.id))
      .flatMap(proximity => getProximityEntitiesAtLocation(point, proximity));
  }, [hiddenProximityIds, getProximityEntitiesAtLocation]);

  return {
    getGroupRadiusProximityLocations,
    getProximitiesAtLocation: getProximityIdsAtLocation,
    isProximityAtLocation,
  };
};
